import { CircleCheck, ExternalLink, Loader2, TriangleAlert } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/atoms/button';
import {
  DIALOG_VIEWPORT_ANCHOR_CLASS,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/atoms/dialog';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from '@/features/auth/context/SessionContext';
import { dropWorkspaceLocationMarker } from '@/features/workspaces/services/workspace.api';
import { useMixpanel } from '@/hooks/useMixpanel';
import { cn } from '@/libs/utils/cn';
import type { DealCreationStep, IDealCreationProgress, ILocation } from '@/types/api';
import { useDealCreation } from '../hooks/useDealCreation';
import {
  DEALS_MIXPANEL_EVENTS,
  type DealCreationAbandonedPayload,
  type DealCreationStepCompletedPayload,
  type DealFormStep,
  type DealRecordMode,
} from '../utils/mixpanelEvents';
import {
  type DealCreationDraft,
  emptyDraft,
  filledOptionalCount,
  isCompanyStepComplete,
  isContactStepComplete,
  isDealStepComplete,
  toCreateRequest,
} from '../utils/dealCreationDraft';
import { landownerTypeFromStored } from '../utils/landownerType';
import { CompanyStep } from './CompanyStep';
import { ContactStep } from './ContactStep';
import { DealStep } from './DealStep';
import { DealWizardSteps } from './DealWizardSteps';

/** The form's own steps, in the order the run performs them. */
const STEPS = ['company', 'contact', 'deal'] as const satisfies readonly DealFormStep[];
type FormStep = (typeof STEPS)[number];

const STEP_LABELS = ['Company', 'Contact', 'Deal'] as const;

/**
 * A run's step maps back onto a form step so a failure returns the BD to the one to look at. The
 * mirror step has no form step of its own: the deal exists by then, and the next sync repairs the
 * mirror, so it lands on the deal step.
 */
const RUN_STEP_TO_FORM_STEP: Record<DealCreationStep, FormStep> = {
  company: 'company',
  contact: 'contact',
  deal: 'deal',
  persist: 'deal',
};

interface CreateDealDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  locationId: string;
  /** Pre-fills the deal name, correctable. */
  locationAddress?: string;
  /**
   * The location's address in the three lines the CRM keeps it in. They pre-fill the new company's
   * address fields, which stay correctable — a line the location does not carry starts empty.
   */
  locationAddressLines?: Pick<ILocation, 'street' | 'postcode' | 'city' | 'country_code'>;
  /** The workspace the location belongs to, whose marker set the landed deal leaves. */
  workspaceId?: string;
  /** Called once with the deal's id when the run lands it, so the caller can show the deal. */
  onCreated?: (dealId: string) => void;
  /** The location's own point. A created company is placed on it, and step 3 shows it read-only. */
  locationCoordinates?: { latitude: number; longitude: number };
  /**
   * The name of the place the location was created from. It pre-fills the POI, and since it was
   * attached by hand the nearby places are not offered over it.
   */
  locationPoiName?: string;
  /** The stored place's icon, which prefills the site type when that icon decides it. */
  locationPoiType?: string;
  dialogContainer?: HTMLElement;
}

/**
 * The three-step deal creation, in HubSpot's own object order. Submitting starts the run and the
 * dialog then follows it: it reports the deal once it lands, and on a failure it returns to the
 * step the run named with every input intact, so the BD corrects rather than retypes.
 */
export function CreateDealDialog({
  open,
  onOpenChange,
  locationId,
  locationAddress,
  locationAddressLines,
  workspaceId,
  onCreated,
  locationCoordinates,
  locationPoiName,
  locationPoiType,
  dialogContainer,
}: CreateDealDialogProps) {
  const [step, setStep] = useState<FormStep>('company');
  const { user } = useSession();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<DealCreationDraft>(() =>
    initialDraft(
      locationCoordinates,
      user?.email,
      locationAddressLines,
      locationPoiName,
      locationPoiType,
    ),
  );
  const { progress, isStarting, error, submit, reset } = useDealCreation(locationId);

  // The form's own funnel. The run's events belong to `useDealCreation`, which owns the run: these
  // are the moves between steps, which is where a BD gives up.
  const trackAction = useMixpanel((s) => s.trackAction);
  const openedAtRef = useRef(Date.now());
  // The step the BD reached, which one who went back is no longer standing on — a drop-off on the
  // company step after seeing the deal step is not the same as never getting past the company.
  const furthestStepRef = useRef<FormStep>('company');
  useEffect(() => {
    trackAction(DEALS_MIXPANEL_EVENTS.CREATION_OPENED, {
      location_id: locationId,
      has_address: locationAddress !== undefined && locationAddress !== '',
    });
    // Mounted only while open (see WorkspaceLocationPopup), so mounting is the opening.
  }, [locationId, locationAddress, trackAction]);

  const goToStep = useCallback(
    (next: FormStep, from: FormStep) => {
      if (STEPS.indexOf(next) > STEPS.indexOf(from)) {
        // The deal step is absent on purpose: it has no record to attach, so it reports no mode
        // rather than a false one.
        const modeOfStep: Partial<Record<FormStep, DealRecordMode>> = {
          company: draft.companyMode,
          contact: draft.contactMode,
        };
        const payload: DealCreationStepCompletedPayload = {
          step: from,
          mode: modeOfStep[from],
          filled_optional_count: filledOptionalCount(draft, from),
        };
        trackAction(DEALS_MIXPANEL_EVENTS.CREATION_STEP_COMPLETED, payload);
        if (STEPS.indexOf(next) > STEPS.indexOf(furthestStepRef.current)) {
          furthestStepRef.current = next;
        }
      } else {
        trackAction(DEALS_MIXPANEL_EVENTS.CREATION_STEP_BACK, { from_step: from, to_step: next });
      }
      setStep(next);
    },
    [draft, trackAction],
  );

  const coordinates = useMemo(
    () =>
      locationCoordinates
        ? `${locationCoordinates.latitude.toFixed(4)}, ${locationCoordinates.longitude.toFixed(4)}`
        : undefined,
    [locationCoordinates],
  );

  // Anything the BD typed or picked, which a close would throw away — the pre-filled owner and
  // address do not count, so an untouched form still closes on the first ask.
  const [edited, setEdited] = useState(false);
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  const patch = useCallback((update: Partial<DealCreationDraft>) => {
    setEdited(true);
    setDraft((prev) => ({ ...prev, ...update }));
  }, []);

  const canContinue = useMemo(() => {
    if (step === 'company') return isCompanyStepComplete(draft);
    if (step === 'contact') return isContactStepComplete(draft);
    return isDealStepComplete(draft);
  }, [draft, step]);

  const close = useCallback(() => {
    // A dialog closed on the deal it landed is not an abandonment: it is the success screen's Done.
    if (progress?.status !== 'created') {
      const payload: DealCreationAbandonedPayload = {
        step,
        furthest_step: furthestStepRef.current,
        elapsed_ms: Date.now() - openedAtRef.current,
        // A run that already failed leaves a correction given up on, not a step never finished.
        after_failure: progress?.status === 'failed',
      };
      trackAction(DEALS_MIXPANEL_EVENTS.CREATION_ABANDONED, payload);
    }
    reset();
    setStep('company');
    setEdited(false);
    setConfirmingDiscard(false);
    setDraft(
      initialDraft(
        locationCoordinates,
        user?.email,
        locationAddressLines,
        locationPoiName,
        locationPoiType,
      ),
    );
    onOpenChange(false);
  }, [
    locationAddressLines,
    locationCoordinates,
    locationPoiName,
    locationPoiType,
    onOpenChange,
    progress?.status,
    reset,
    step,
    trackAction,
    user?.email,
  ]);

  // The run landing the deal is what replaces this dialog with the deal itself, so the caller is
  // told once — a repeated created reading must not reopen anything.
  const reported = useRef(false);
  useEffect(() => {
    if (reported.current) return;
    const dealId = progress?.status === 'created' ? progress.deal_id : undefined;
    if (!dealId) return;
    reported.current = true;
    // The location left the workspace as a consequence of the run, not of anything this client
    // asked for, so the marker set is corrected in place rather than refetched — otherwise the
    // pin lingers until the next read.
    if (workspaceId) dropWorkspaceLocationMarker(queryClient, workspaceId, locationId);
    onCreated?.(dealId);
  }, [onCreated, progress, queryClient, workspaceId, locationId]);

  const isWatching = isStarting || progress?.status === 'running';

  // Escape, the overlay and the close cross all land here, which is why the guard sits on the
  // closing rather than on the keystroke: a filled-in form asks before it is thrown away.
  const requestClose = useCallback(() => {
    if (edited && progress === null && !isStarting) {
      setConfirmingDiscard(true);
      return;
    }
    close();
  }, [close, edited, isStarting, progress]);
  const created = progress?.status === 'created' ? progress : null;
  const failed = progress?.status === 'failed' ? progress : null;

  // A run that failed returns the BD to its step, with everything they typed still there.
  const failedStep = failed?.step;
  useEffect(() => {
    if (failedStep) setStep(RUN_STEP_TO_FORM_STEP[failedStep]);
  }, [failedStep]);

  const banner = error?.message ?? failed?.message ?? (failed ? 'The creation stopped.' : null);

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : requestClose())}>
      <DialogContent
        dialogContainer={dialogContainer}
        overlayClassName={dialogContainer ? 'absolute inset-0' : undefined}
        className={cn(
          'flex max-h-[min(90dvh,40rem)] w-[min(560px,calc(100vw-2rem))] max-w-[min(560px,calc(100vw-2rem))] flex-col gap-0 overflow-hidden p-4 sm:p-7',
          dialogContainer ? 'absolute' : DIALOG_VIEWPORT_ANCHOR_CLASS,
        )}
      >
        <DialogHeader className="shrink-0 gap-1 pr-8">
          <DialogTitle className="text-[21px] font-normal text-primary-700">
            Create deal
          </DialogTitle>
          <DialogDescription className="text-[13px] leading-[18px] text-primary-600">
            Company, contact, then the deal — HubSpot&rsquo;s own order.
          </DialogDescription>
        </DialogHeader>

        {created ? (
          <div className="mt-5 flex flex-col items-start gap-3">
            <p className="flex items-center gap-2 text-sm">
              <CircleCheck className="size-4 text-brand-800" aria-hidden />
              {created.deal?.name ?? 'The deal'} was created.
            </p>
            {created.deal?.hubspot_url && (
              <a
                className="flex items-center gap-1.5 text-sm text-brand-800 underline"
                href={created.deal.hubspot_url}
                target="_blank"
                rel="noreferrer"
                onClick={() =>
                  trackAction(DEALS_MIXPANEL_EVENTS.HUBSPOT_OPENED, {
                    deal_id: created.deal_id ?? '',
                    stage: created.deal?.stage,
                    origin: 'creation_success',
                  })
                }
              >
                Open it in HubSpot
                <ExternalLink className="size-3.5" aria-hidden />
              </a>
            )}
            <DialogFooter className="mt-5 flex w-full gap-2.5">
              <Button type="button" className="h-11 flex-1" onClick={close}>
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            <div className="mt-5">
              <DealWizardSteps labels={STEP_LABELS} current={STEPS.indexOf(step)} />
            </div>

            {banner && (
              <p className="mt-3.5 flex items-start gap-2.5 rounded-lg bg-[#FEECE9] px-3.5 py-3 text-[12.5px] leading-[17px] text-[#A61F0D]">
                <TriangleAlert className="mt-px size-4 shrink-0" aria-hidden />
                <span>{banner}</span>
              </p>
            )}

            <div className="mt-5 min-h-0 flex-1 overflow-y-auto" data-testid="deal-creation-body">
              {isWatching ? (
                <p className="flex items-center gap-2 text-sm text-primary-500">
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  {creationLabel(progress)}
                </p>
              ) : (
                <>
                  {step === 'company' && <CompanyStep draft={draft} onChange={patch} />}
                  {step === 'contact' && <ContactStep draft={draft} onChange={patch} />}
                  {step === 'deal' && (
                    <DealStep
                      locationId={locationId}
                      draft={draft}
                      onChange={patch}
                      coordinates={coordinates}
                      addressLines={locationAddressLines}
                      nearbyPlacesAt={locationPoiName ? undefined : locationCoordinates}
                    />
                  )}
                </>
              )}
            </div>

            {confirmingDiscard ? (
              <>
                <p className="mt-6 text-[13px] leading-[18px] text-primary-600">
                  Closing now loses everything you filled in.
                </p>
                <DialogFooter className="mt-3 shrink-0 flex-col gap-2.5 sm:flex-row">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 flex-1"
                    onClick={() => setConfirmingDiscard(false)}
                  >
                    Keep editing
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 flex-1 text-destructive"
                    onClick={close}
                  >
                    Discard
                  </Button>
                </DialogFooter>
              </>
            ) : (
              <DialogFooter className="mt-6 shrink-0 flex-col gap-2.5 sm:flex-row">
                {step === 'company' ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 flex-1"
                    disabled={isWatching}
                    onClick={requestClose}
                  >
                    Cancel
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="h-11 flex-1"
                    disabled={isWatching}
                    onClick={() => goToStep(STEPS[STEPS.indexOf(step) - 1], step)}
                  >
                    Back
                  </Button>
                )}
                {step === 'deal' ? (
                  <Button
                    type="button"
                    className="h-11 flex-1"
                    disabled={!canContinue || isWatching}
                    onClick={() => void submit(toCreateRequest(draft))}
                  >
                    {isWatching && <Loader2 className="size-3.5 animate-spin" aria-hidden />}
                    {isWatching ? 'Creating…' : 'Create deal'}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    className="h-11 flex-1"
                    disabled={!canContinue}
                    onClick={() => goToStep(STEPS[STEPS.indexOf(step) + 1], step)}
                  >
                    Continue
                  </Button>
                )}
              </DialogFooter>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * What the BD reads while the run goes. A reading that names its step says so; one that does not
 * still says the run has started, because a run reachable enough to answer is past starting — the
 * two used to render the same sentence, which made a stream delivering nothing look identical to
 * a run whose step the portal had not published yet.
 */
function creationLabel(progress: IDealCreationProgress | null): string {
  if (!progress) return 'Starting the creation…';
  if (!progress.step) return 'Creating the records in HubSpot…';
  return progress.step === 'persist'
    ? 'Recording the deal in SpotOn…'
    : `Creating the ${progress.step}…`;
}

function initialDraft(
  locationCoordinates?: { latitude: number; longitude: number },
  ownerEmail?: string,
  locationAddressLines?: Pick<ILocation, 'street' | 'postcode' | 'city' | 'country_code'>,
  locationPoiName?: string,
  locationPoiType?: string,
): DealCreationDraft {
  const draft = emptyDraft(locationPoiName ?? '', locationAddressLines?.city ?? '');
  draft.landownerType = landownerTypeFromStored(locationPoiType);
  // The BD creating the deal is the likely owner, so the field starts on them and stays editable.
  // It is only a default: an address the CRM cannot resolve still leaves the deal unowned rather
  // than being quietly reassigned to whoever was signed in.
  if (ownerEmail) draft.ownerEmail = ownerEmail;
  draft.company.street = locationAddressLines?.street ?? '';
  draft.company.postcode = locationAddressLines?.postcode ?? '';
  draft.company.city = locationAddressLines?.city ?? '';
  // The country is the location's and has no field: it is shown read-only and written as given.
  draft.company.country = locationAddressLines?.country_code ?? '';
  if (locationCoordinates) {
    draft.company.latitude = locationCoordinates.latitude;
    draft.company.longitude = locationCoordinates.longitude;
  }
  return draft;
}
